import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { RegisterResult, SessionUser } from '@katsquare/types';
import { Button, Card, ErrorText, Field, Input, Spinner } from '../components/ui';
import { ApiRequestError, getRegistrationStatus, login, register, resendVerification } from '../lib/api';
import { SESSION_KEY } from '../features/auth/useSession';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const isRegister = mode === 'register';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');
  const authQuery = params.toString() ? `?${params.toString()}` : '';

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const dest = (u: SessionUser) => (inviteToken ? `/invite/${inviteToken}` : u.isSuperAdmin ? '/admin' : '/app');

  const mutation = useMutation<SessionUser | RegisterResult, ApiRequestError>({
    mutationFn: () =>
      isRegister
        ? register({ email, name, password, inviteToken: inviteToken ?? undefined })
        : login({ email, password }),
    onSuccess: (data) => {
      if ('status' in data) {
        // Register result: either signed in (invited) or needs verification.
        if (data.status === 'verify_sent') {
          setSentTo(data.email);
          return;
        }
        queryClient.setQueryData(SESSION_KEY, data.user);
        // Invited registration is auto-joined to the workspace server-side, so
        // go straight into the app rather than bouncing back to the invite page.
        navigate(data.user.isSuperAdmin ? '/admin' : '/app', { replace: true });
      } else {
        queryClient.setQueryData(SESSION_KEY, data);
        navigate(dest(data), { replace: true });
      }
    },
  });

  const resend = useMutation({
    mutationFn: () => resendVerification(email),
    onSuccess: () => setSentTo(email),
  });

  const unverified = mutation.error?.body?.code === 'auth.email_unverified';

  // Public sign-ups can be paused by an admin. Invites always bypass the gate.
  const regStatus = useQuery({
    queryKey: ['registrationStatus'],
    queryFn: getRegistrationStatus,
    enabled: isRegister && !inviteToken,
  });
  const registrationClosed = isRegister && !inviteToken && regStatus.data?.open === false;

  if (registrationClosed) {
    return (
      <Shell>
        <Card className="p-6 text-center">
          <h1 className="mb-1 text-lg font-semibold text-neutral-900">
            Registrations paused
          </h1>
          <p className="mb-5 text-sm text-neutral-500">
            We are not currently accepting new account registrations. Please check back later.
          </p>
          <p className="text-sm text-neutral-500">
            Already have an account?{' '}
            <Link className="font-medium text-neutral-900 underline" to="/login">
              Sign in
            </Link>
          </p>
        </Card>
      </Shell>
    );
  }

  // ── "Check your email" confirmation screen ────────────────────────────────
  if (sentTo) {
    return (
      <Shell>
        <Card className="p-6 text-center">
          <h1 className="mb-1 text-lg font-semibold text-neutral-900">Verify your email</h1>
          <p className="mb-5 text-sm text-neutral-500">
            We sent a verification link to <strong>{sentTo}</strong>. Click it to activate your account, then
            sign in.
          </p>
          <Button
            variant="ghost"
            className="w-full border border-neutral-200"
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
          >
            {resend.isPending ? <Spinner /> : 'Resend link'}
          </Button>
          <p className="mt-4 text-sm text-neutral-500">
            <Link className="font-medium text-neutral-900 underline" to="/login">
              Back to sign in
            </Link>
          </p>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card className="p-6">
        <h1 className="mb-1 text-lg font-semibold text-neutral-900">
          {isRegister ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="mb-5 text-sm text-neutral-500">
          {isRegister ? 'Start reviewing proofs with your team.' : 'Sign in to continue.'}
        </p>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          {isRegister && (
            <Field label="Name">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={120}
                required
              />
            </Field>
          )}
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              minLength={isRegister ? 10 : undefined}
              required
            />
          </Field>

          {!isRegister && (
            <div className="-mt-2 text-right">
              <Link to="/forgot-password" className="text-xs font-medium text-neutral-500 hover:text-neutral-900">
                Forgot password?
              </Link>
            </div>
          )}

          {mutation.isError && <ErrorText>{mutation.error.message}</ErrorText>}
          {unverified && (
            <button
              type="button"
              onClick={() => resend.mutate()}
              disabled={resend.isPending}
              className="text-left text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              {resend.isPending ? 'Sending…' : 'Resend verification email'}
            </button>
          )}

          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? <Spinner /> : isRegister ? 'Create account' : 'Sign in'}
          </Button>
        </form>
      </Card>

      <p className="mt-4 text-center text-sm text-neutral-500">
        {isRegister ? (
          <>
            Already have an account?{' '}
            <Link className="font-medium text-neutral-900 underline" to={`/login${authQuery}`}>
              Sign in
            </Link>
          </>
        ) : (
          <>
            New here?{' '}
            <Link className="font-medium text-neutral-900 underline" to={`/register${authQuery}`}>
              Create an account
            </Link>
          </>
        )}
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center">
          <span className="text-lg font-semibold tracking-tight text-neutral-900">
            KatSquare Draft
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
